import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, Box, ShoppingCart, ArrowRight } from "lucide-react";
import client, { displayName, priceLabel, hasPrice } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { TechnicianBar } from "@/components/TechnicianBar";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const Cart = () => {
  const { cart, setQty, removeFromCart, clearCart } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!cart.length) { setItems([]); return; }
    client.post("/products/by-skus", { skus: cart.map((i) => i.sku) }).then((r) => setItems(r.data.items));
  }, [cart.map((i) => i.sku).join(",")]);

  const qtyOf = (sku) => cart.find((i) => i.sku === sku)?.qty || 0;
  const totalUnits = cart.reduce((n, i) => n + i.qty, 0);
  const estTotal = items.reduce((s, p) => s + (hasPrice(p) ? Number(p.admin_price) * qtyOf(p.sku) : 0), 0);
  const allPriced = items.length > 0 && items.every((p) => hasPrice(p));
  const goCheckout = () => navigate(user ? "/checkout" : "/login?next=/checkout");

  return (
    <div className="App min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 w-full py-8 flex-1">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-graphite">Cart & Quick Reorder</h1>
          {cart.length > 0 && <Button variant="ghost" size="sm" data-testid="clear-cart" onClick={clearCart} className="text-steel-light">Clear all</Button>}
        </div>

        {cart.length === 0 ? (
          <div className="grid place-items-center py-24 text-center">
            <ShoppingCart className="w-12 h-12 text-steel-light mb-4" />
            <p className="text-steel font-medium">Your cart is empty</p>
            <p className="text-sm text-steel-light mb-4">Add parts from the catalogue to build an order.</p>
            <Link to="/catalogue"><Button className="bg-orange hover:bg-orange-hover">Browse Catalogue</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {items.map((p) => (
                <div key={p.sku} data-testid={`cart-item-${p.sku}`} className="flex items-center gap-4 p-3 bg-white border border-border rounded-md">
                  <Link to={`/product/${encodeURIComponent(p.sku)}`} className="w-16 h-16 rounded gp-card-img grid place-items-center p-1.5 shrink-0">
                    {p.main_image ? <img src={p.main_image} alt="" className="max-h-full object-contain mix-blend-multiply" /> : <Box className="w-6 h-6 text-steel-light" />}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="mono text-xs text-orange">{p.sku}</div>
                    <Link to={`/product/${encodeURIComponent(p.sku)}`} className="text-sm font-medium text-graphite line-clamp-1 hover:text-orange">{displayName(p)}</Link>
                    <div className="text-xs text-steel-light">{p.brand || p.category} · {priceLabel(p)}</div>
                  </div>
                  <div className="flex items-center border border-border rounded-md h-9">
                    <button data-testid={`cart-minus-${p.sku}`} onClick={() => setQty(p.sku, qtyOf(p.sku) - 1)} className="w-8 grid place-items-center text-steel"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="mono w-8 text-center text-sm">{qtyOf(p.sku)}</span>
                    <button data-testid={`cart-plus-${p.sku}`} onClick={() => setQty(p.sku, qtyOf(p.sku) + 1)} className="w-8 grid place-items-center text-steel"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button data-testid={`cart-remove-${p.sku}`} onClick={() => removeFromCart(p.sku)} className="w-9 h-9 grid place-items-center text-steel-light hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="border border-border rounded-lg p-5 sticky top-24">
                <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Order Summary</div>
                <div className="flex justify-between text-sm py-1"><span className="text-steel-light">Line items</span><span className="mono">{cart.length}</span></div>
                <div className="flex justify-between text-sm py-1"><span className="text-steel-light">Total units</span><span className="mono">{totalUnits}</span></div>
                <div className="flex justify-between text-sm py-1 border-t border-border mt-2 pt-3"><span className="text-steel-light">Estimated total</span><span className="font-semibold text-graphite">{allPriced ? `$${estTotal.toFixed(2)} CAD` : "Request Quote"}</span></div>
                <Button data-testid="checkout-btn" onClick={goCheckout} className="w-full mt-4 h-11 bg-orange hover:bg-orange-hover">
                  Proceed to Checkout <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-steel-light mt-3 text-center">{user ? "Place an order or request a quote." : "Sign in to place an order or request a quote."}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
      <TechnicianBar />
    </div>
  );
};

export default Cart;
